import { useState } from 'react';
import { Card } from '@reflex/ui/components/ui/card';
import {
  useToggleAgentProvider,
  useToggleAgentModel,
  useSetAgentDefault,
  useSetAgentTypeEnabled,
} from '@/hooks/config/useAgentModelPolicy';
import type {
  AdminAgentModelEntry,
  AdminLaunchableAgentEntry,
  DiscoveredModel,
  AgentModelPolicy,
} from '@reflex/shared';
import { AgentPolicyCardHeader, type PolicyScope } from './AgentPolicyCardHeader';
import { EffectiveDefaultDisplay } from './EffectiveDefaultDisplay';
import { ProvidersSection } from './ProvidersSection';
import { AdminDefaultsSection } from './AdminDefaultsSection';
import { ModelsByProviderSection } from './ModelsByProviderSection';

export interface AgentPolicyCardProps {
  entry: AdminAgentModelEntry;
  scope: PolicyScope;
  launchable?: AdminLaunchableAgentEntry;
  readOnly?: boolean;
}

export function AgentPolicyCard({
  entry,
  scope,
  launchable,
  readOnly = false,
}: AgentPolicyCardProps) {
  const initiallyExpanded = launchable?.effectiveEnabled ?? true;
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const toggleProvider = useToggleAgentProvider();
  const toggleModel = useToggleAgentModel();
  const setDefault = useSetAgentDefault();
  const toggleAgentEnabled = useSetAgentTypeEnabled();

  // Use unfiltered data from the admin response
  const { providers, discoveredModels = [] } = entry;

  const displayPolicy: AgentModelPolicy = entry.effectivePolicy;

  const isProviderEnabled = (providerId: string): boolean => {
    const explicit = displayPolicy.providers[providerId];
    return explicit !== false;
  };

  const isModelEnabled = (modelId: string): boolean => {
    const explicit = displayPolicy.models[modelId];
    return explicit !== false;
  };

  const modelsByProvider = providers.reduce(
    (acc, provider) => {
      acc[provider.id] = discoveredModels.filter((m) => m.providerId === provider.id);
      return acc;
    },
    {} as Record<string, DiscoveredModel[]>,
  );

  const isPending = toggleProvider.isPending || toggleModel.isPending || setDefault.isPending;

  // Filter to only enabled providers for the Admin Defaults dropdown
  const enabledProviders = providers.filter((p) => isProviderEnabled(p.id));

  // Current default values from the displayed policy
  const currentDefaultProvider = displayPolicy.default.providerId;
  const currentDefaultModel = displayPolicy.default.modelId;

  // Saved provider ID from the correct scope
  const savedProviderId = entry.effectivePolicy.default.providerId;

  const enabledModelsCount = discoveredModels.filter(
    (m) => isModelEnabled(m.id) && isProviderEnabled(m.providerId),
  ).length;

  return (
    <Card className="overflow-hidden">
      <AgentPolicyCardHeader
        entry={entry}
        launchable={launchable}
        isExpanded={isExpanded}
        onToggleExpanded={() => setIsExpanded(!isExpanded)}
        enabledModelsCount={enabledModelsCount}
        totalDiscoveredModels={discoveredModels.length}
        isAgentTogglePending={toggleAgentEnabled.isPending}
        readOnly={readOnly}
        onToggleAvailability={(checked) => {
          if (readOnly) return;
          toggleAgentEnabled.mutate({
            agentType: entry.agentType,
            enabled: checked,
            scope,
          });
        }}
      />

      {isExpanded && (
        <div className="border-t px-3 pb-4 space-y-5">
          {entry.effectiveDefault && (
            <EffectiveDefaultDisplay
              effectiveDefault={entry.effectiveDefault}
              providers={providers}
              discoveredModels={discoveredModels}
            />
          )}

          <AdminDefaultsSection
            enabledProviders={enabledProviders}
            discoveredModels={discoveredModels}
            savedProviderId={savedProviderId}
            currentDefaultProvider={currentDefaultProvider}
            currentDefaultModel={currentDefaultModel}
            isProviderEnabled={isProviderEnabled}
            isPending={isPending}
            readOnly={readOnly}
            onSetDefault={(defaults) => {
              if (readOnly) return;
              setDefault.mutate({
                agentType: entry.agentType,
                defaults,
                scope,
              });
            }}
          />

          <ProvidersSection
            providers={providers}
            modelsByProvider={modelsByProvider}
            isProviderEnabled={isProviderEnabled}
            isPending={isPending}
            readOnly={readOnly}
            onToggleProvider={(providerId, enabled) => {
              if (readOnly) return;
              toggleProvider.mutate({
                agentType: entry.agentType,
                providerId,
                enabled,
                scope,
              });
            }}
          />

          <ModelsByProviderSection
            providers={providers}
            modelsByProvider={modelsByProvider}
            isProviderEnabled={isProviderEnabled}
            isModelEnabled={isModelEnabled}
            isPending={isPending}
            hasDiscoveredModels={discoveredModels.length > 0}
            readOnly={readOnly}
            onToggleModel={(modelId, enabled) => {
              if (readOnly) return;
              toggleModel.mutate({
                agentType: entry.agentType,
                modelId,
                enabled,
                scope,
              });
            }}
          />
        </div>
      )}
    </Card>
  );
}
